import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import CalendarDayCell from './CalendarDayCell'
import { rem } from 'polished'
import { calcGridPositionsForVerticalMonth, getCalendarMonthEmptyCells, getLocalDate, getMondayFirstWeekdayIndex } from 'lib/datetime/calendarUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import { getWeekdayNames, selectDateFormattingLocale } from 'lib/datetime/dateUtils'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`

const Grid = styled(BaseGrid)`
  position: relative;
  margin-top: ${rem(8)};
`

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
    1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
    1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset,
    0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset;
`

interface Props {
  onDatesChange: (day: App.BedbankCalendarDay) => void
  checkIn?: string
  checkOut?: string
  days: Array<App.BedbankCalendarDay>
  className?: string
  duration: number
  flights: boolean
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
}

function CalendarDaysMonth(props: Props) {
  const {
    className,
    days,
    checkIn,
    checkOut,
    duration,
    flights,
    onDatesChange,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props

  const { currentRegionCode, locale: geoLocale } = useContext(GeoContext)
  const dateFormattingLocale = selectDateFormattingLocale(geoLocale, currentRegionCode)
  const dayNames = useMemo(() => {
    const weekdayNames = getWeekdayNames(dateFormattingLocale)
    return [
      weekdayNames.mon,
      weekdayNames.tue,
      weekdayNames.wed,
      weekdayNames.thu,
      weekdayNames.fri,
      weekdayNames.sat,
      weekdayNames.sun,
    ]
  }, [dateFormattingLocale])

  const firstDate = days[0]

  const startCellStyle = useMemo(() => ({
    gridColumnStart: getMondayFirstWeekdayIndex(getLocalDate(firstDate.checkIn)) + 1,
  }), [firstDate])

  const emptyCells = useMemo(() => getCalendarMonthEmptyCells(firstDate), [firstDate])

  const betweenRange = useMemo(() => {
    if (checkIn && checkOut) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(checkIn),
        getLocalDate(checkOut),
      ).range
    }
    return undefined
  }, [checkIn, checkOut, firstDate])

  const onDayClick = useCallback((day: App.BedbankCalendarDay) => {
    onDatesChange(day)
  }, [onDatesChange])

  return (
    <div className={className}>
      <BaseGrid>
        {dayNames.map((day) => (
          <Caption variant="large" as="abbr" title={day.long} key={day.long}>{day.short}</Caption>
        ))}
      </BaseGrid>
      <Grid>
        {emptyCells.map((cell, index) =>
          <EmptyCell style={{ gridColumnStart: cell.column }} key={index}>
            <Heading colour="light-foreground-alt" variant="heading6">{cell.day}</Heading>
          </EmptyCell>,
        )}
        {days.map((day, index) => <CalendarDayCell
          key={day.checkIn}
          duration={duration}
          price={day.price}
          propertyFees={day.propertyFees}
          style={(index === 0 && emptyCells.length === 0) ? startCellStyle : undefined}
          day={day.day}
          flights={flights}
          flightPrice={day.flightPrice}
          hasPromo={day.hasPromo}
          isCorporate={day.isCorporate}
          isCheckIn={day.checkIn === checkIn}
          isCheckOut={day.checkIn === checkOut}
          isBetweenDay={!!(betweenRange && (index + 1) >= betweenRange.start && index < (betweenRange.end - 1))}
          onClick={() => onDayClick(day)}
          displayPricingAsPerPerson={displayPricingAsPerPerson}
          adultsNumber={adultsNumber}
          roomsNumber={roomsNumber}
        />)}
      </Grid>
    </div>
  )
}

export default withOfferPageState((state) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
}), CalendarDaysMonth)
